import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeftIcon, PackageIcon, CheckIcon, AlertCircleIcon, ScanLineIcon, XCircleIcon, CheckCircleIcon, ClipboardListIcon, ScaleIcon, ArrowRightIcon, RefreshCwIcon, AlertTriangleIcon } from 'lucide-react';
interface TKLineProcessProps {
  lineId: string;
  weighingMode: 'line' | 'output';
  onBack: () => void;
  onWeighingRequired: (materialId: string, orderId: string) => void;
  onBatchComplete: () => void;
}
interface Material {
  id: string;
  name: string;
  orderId: string;
  orderName: string;
  quantity: number;
  unit: string;
  filled: boolean;
  lotId: string;
  lotSize: number;
  boxesProcessed: number;
  lotValidated: boolean;
  weight?: number; // Gewicht für Linienwaage
}
// Interface für eine einzelne Waage
interface Scale {
  id: number;
  name: string;
  taraWeight: number | null;
  bruttoWeight: number | null;
  nettoWeight: number | null;
  isWeighingTara: boolean;
  isWeighingBrutto: boolean;
  isWithinTolerance: boolean | null;
}
export function TKLineProcess({
  lineId,
  weighingMode,
  onBack,
  onWeighingRequired,
  onBatchComplete
}: TKLineProcessProps) {
  const [currentMaterialIndex, setCurrentMaterialIndex] = useState(0);
  const [scannedCode, setScannedCode] = useState('');
  const [isValidated, setIsValidated] = useState(false);
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [filledMaterials, setFilledMaterials] = useState<string[]>([]);
  const [showSuccessFeedback, setShowSuccessFeedback] = useState(false);
  const [batchNumber, setBatchNumber] = useState<string>('');
  const [isWeighing, setIsWeighing] = useState(false);
  const [weight, setWeight] = useState<number | null>(null);
  const [isWithinTolerance, setIsWithinTolerance] = useState<boolean | null>(null);
  // Neue States für Tara-Wiegung
  const [taraWeight, setTaraWeight] = useState<number | null>(null);
  const [isWeighingTara, setIsWeighingTara] = useState(false);
  const [weighingStep, setWeighingStep] = useState<'tara' | 'brutto'>('tara');
  // Neuer State für mehrere Waagen
  const [scales, setScales] = useState<Scale[]>([{
    id: 1,
    name: 'Waage 1',
    taraWeight: null,
    bruttoWeight: null,
    nettoWeight: null,
    isWeighingTara: false,
    isWeighingBrutto: false,
    isWithinTolerance: null
  }, {
    id: 2,
    name: 'Waage 2',
    taraWeight: null,
    bruttoWeight: null,
    nettoWeight: null,
    isWeighingTara: false,
    isWeighingBrutto: false,
    isWithinTolerance: null
  }, {
    id: 3,
    name: 'Waage 3',
    taraWeight: null,
    bruttoWeight: null,
    nettoWeight: null,
    isWeighingTara: false,
    isWeighingBrutto: false,
    isWithinTolerance: null
  }, {
    id: 4,
    name: 'Waage 4',
    taraWeight: null,
    bruttoWeight: null,
    nettoWeight: null,
    isWeighingTara: false,
    isWeighingBrutto: false,
    isWithinTolerance: null
  }, {
    id: 5,
    name: 'Waage 5',
    taraWeight: null,
    bruttoWeight: null,
    nettoWeight: null,
    isWeighingTara: false,
    isWeighingBrutto: false,
    isWithinTolerance: null
  }]);
  // State für die Anzeige, ob alle Waagen gleichzeitig im Prozess sind
  const [isAllScalesTaring, setIsAllScalesTaring] = useState(false);
  const [isAllScalesWeighing, setIsAllScalesWeighing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Verwenden eines State für die Materialien, damit die UI aktualisiert werden kann
  const [materials, setMaterials] = useState<Material[]>([{
    id: 'R061307',
    name: 'MSC Alaska Seelachs',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    quantity: 200.0,
    unit: 'kg',
    filled: false,
    lotId: 'L2023-110',
    lotSize: 5,
    boxesProcessed: 0,
    lotValidated: false
  }, {
    id: 'R053408',
    name: 'Hähnchen Brust St gebraten',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    quantity: 200.0,
    unit: 'kg',
    filled: false,
    lotId: 'L2023-111',
    lotSize: 5,
    boxesProcessed: 0,
    lotValidated: false
  }, {
    id: 'R161215',
    name: 'Langkornreis',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    quantity: 150.0,
    unit: 'kg',
    filled: false,
    lotId: 'L2023-112',
    lotSize: 5,
    boxesProcessed: 0,
    lotValidated: false
  }, {
    id: 'R090533',
    name: 'Erbsen',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    quantity: 100.0,
    unit: 'kg',
    filled: false,
    lotId: 'L2023-113',
    lotSize: 3,
    boxesProcessed: 0,
    lotValidated: false
  }]);
  const currentMaterial = materials[currentMaterialIndex];
  const progress = filledMaterials.length / materials.length * 100;
  // Prüfen, ob alle Materialien verarbeitet wurden
  useEffect(() => {
    if (filledMaterials.length === materials.length) {
      // Alle Materialien wurden verarbeitet
      setTimeout(() => {
        onBatchComplete();
      }, 1000);
    }
  }, [filledMaterials, materials.length, onBatchComplete]);
  // Prüfen, ob das aktuelle Lot bereits validiert wurde
  useEffect(() => {
    // Wenn das Material wechselt, prüfen wir, ob es bereits validiert wurde
    if (currentMaterial.lotValidated) {
      setIsValidated(true);
      setBatchNumber(generateBatchNumber(currentMaterial.id));
    } else {
      setIsValidated(false);
      setBatchNumber('');
    }
    // Zurücksetzen der Wiegedaten bei Materialwechsel
    resetAllScales();
    setWeighingStep('tara');
  }, [currentMaterialIndex, currentMaterial.lotValidated]);
  // Funktion zum Zurücksetzen aller Waagen
  const resetAllScales = () => {
    setScales(scales.map(scale => ({
      ...scale,
      taraWeight: null,
      bruttoWeight: null,
      nettoWeight: null,
      isWeighingTara: false,
      isWeighingBrutto: false,
      isWithinTolerance: null
    })));
  };
  // Funktion für die Tara-Wiegung einer einzelnen Waage
  const startTaraWeighing = (scaleId: number) => {
    setScales(scales.map(scale => scale.id === scaleId ? {
      ...scale,
      isWeighingTara: true
    } : scale));
    // Simuliere den Wiegeprozess
    setTimeout(() => {
      // Zufälliges Tara-Gewicht zwischen 0.8 und 1.2 kg
      const randomTara = 0.8 + Math.random() * 0.4;
      setScales(scales.map(scale => scale.id === scaleId ? {
        ...scale,
        taraWeight: parseFloat(randomTara.toFixed(2)),
        isWeighingTara: false
      } : scale));
    }, 1500);
  };
  // Funktion für die Tara-Wiegung aller Waagen gleichzeitig
  const startAllTaraWeighing = () => {
    setIsAllScalesTaring(true);
    // Alle Waagen als "im Wiegeprozess" markieren
    setScales(scales.map(scale => ({
      ...scale,
      isWeighingTara: true
    })));
    // Simuliere den Wiegeprozess für alle Waagen mit leicht unterschiedlichen Zeiten
    scales.forEach((scale, index) => {
      setTimeout(() => {
        // Zufälliges Tara-Gewicht zwischen 0.8 und 1.2 kg
        const randomTara = 0.8 + Math.random() * 0.4;
        setScales(prevScales => prevScales.map(s => s.id === scale.id ? {
          ...s,
          taraWeight: parseFloat(randomTara.toFixed(2)),
          isWeighingTara: false
        } : s));
        // Wenn die letzte Waage fertig ist, den Gesamtstatus zurücksetzen
        if (index === scales.length - 1) {
          setIsAllScalesTaring(false);
          setWeighingStep('brutto');
        }
      }, 1000 + index * 300); // Gestaffelte Zeiten für realistischeres Verhalten
    });
  };
  // Funktion für die Brutto-Wiegung einer einzelnen Waage
  const startBruttoWeighing = (scaleId: number) => {
    setScales(scales.map(scale => scale.id === scaleId ? {
      ...scale,
      isWeighingBrutto: true
    } : scale));
    // Simuliere den Wiegeprozess
    setTimeout(() => {
      const scale = scales.find(s => s.id === scaleId);
      if (!scale || scale.taraWeight === null) return;
      // Zufälliges Gewicht innerhalb oder außerhalb der Toleranz
      const targetWeight = currentMaterial.quantity / scales.length; // Teile das Gesamtgewicht auf die Waagen auf
      const tolerance = targetWeight * 0.05; // 5% Toleranz
      // Brutto-Gewicht = Ziel-Nettogewicht + Tara mit zufälliger Abweichung
      const randomBrutto = targetWeight + scale.taraWeight + (Math.random() * (tolerance * 1.2) - tolerance * 0.6);
      const bruttoWeight = parseFloat(randomBrutto.toFixed(2));
      // Berechne das Netto-Gewicht (Brutto - Tara)
      const nettoWeight = parseFloat((bruttoWeight - scale.taraWeight).toFixed(2));
      // Prüfe, ob innerhalb der Toleranz (basierend auf Netto-Gewicht)
      const isValid = Math.abs(nettoWeight - targetWeight) <= tolerance;
      setScales(prevScales => prevScales.map(s => s.id === scaleId ? {
        ...s,
        bruttoWeight,
        nettoWeight,
        isWeighingBrutto: false,
        isWithinTolerance: isValid
      } : s));
    }, 2000);
  };
  // Funktion für die Brutto-Wiegung aller Waagen gleichzeitig
  const startAllBruttoWeighing = () => {
    // Prüfen, ob alle Waagen ein Tara-Gewicht haben
    const allHaveTara = scales.every(scale => scale.taraWeight !== null);
    if (!allHaveTara) {
      return; // Nicht fortfahren, wenn nicht alle Waagen tariert sind
    }
    setIsAllScalesWeighing(true);
    // Alle Waagen als "im Wiegeprozess" markieren
    setScales(scales.map(scale => ({
      ...scale,
      isWeighingBrutto: true
    })));
    // Simuliere den Wiegeprozess für alle Waagen mit leicht unterschiedlichen Zeiten
    scales.forEach((scale, index) => {
      setTimeout(() => {
        if (scale.taraWeight === null) return;
        // Zufälliges Gewicht innerhalb oder außerhalb der Toleranz
        const targetWeight = currentMaterial.quantity / scales.length; // Teile das Gesamtgewicht auf die Waagen auf
        const tolerance = targetWeight * 0.05; // 5% Toleranz
        // Brutto-Gewicht = Ziel-Nettogewicht + Tara mit zufälliger Abweichung
        const randomBrutto = targetWeight + scale.taraWeight + (Math.random() * (tolerance * 1.2) - tolerance * 0.6);
        const bruttoWeight = parseFloat(randomBrutto.toFixed(2));
        // Berechne das Netto-Gewicht (Brutto - Tara)
        const nettoWeight = parseFloat((bruttoWeight - scale.taraWeight).toFixed(2));
        // Prüfe, ob innerhalb der Toleranz (basierend auf Netto-Gewicht)
        const isValid = Math.abs(nettoWeight - targetWeight) <= tolerance;
        setScales(prevScales => prevScales.map(s => s.id === scale.id ? {
          ...s,
          bruttoWeight,
          nettoWeight,
          isWeighingBrutto: false,
          isWithinTolerance: isValid
        } : s));
        // Wenn die letzte Waage fertig ist, den Gesamtstatus zurücksetzen
        if (index === scales.length - 1) {
          setIsAllScalesWeighing(false);
        }
      }, 1500 + index * 400); // Gestaffelte Zeiten für realistischeres Verhalten
    });
  };
  // Prüfe, ob alle Waagen innerhalb der Toleranz sind
  const areAllScalesWithinTolerance = () => {
    return scales.every(scale => scale.isWithinTolerance === true);
  };
  // Prüfe, ob alle Waagen ein Netto-Gewicht haben
  const allScalesHaveNettoWeight = () => {
    return scales.every(scale => scale.nettoWeight !== null);
  };
  // Berechne das Gesamtgewicht aller Waagen
  const calculateTotalNettoWeight = () => {
    return scales.reduce((sum, scale) => sum + (scale.nettoWeight || 0), 0);
  };
  const [confirmedBoxes, setConfirmedBoxes] = useState<number>(0);
  const [showUnloadScaleMessage, setShowUnloadScaleMessage] = useState(false);
  const handleMaterialFilled = () => {
    if (weighingMode === 'line') {
      // Bei Linienwaage muss zuerst gewogen werden
      if (!allScalesHaveNettoWeight() || !areAllScalesWithinTolerance()) {
        return;
      }
      // Berechne das Gesamtgewicht aller Waagen
      const totalNettoWeight = calculateTotalNettoWeight();
      // Show the unload scale message
      setShowUnloadScaleMessage(true);
      // Aktualisiere sofort den Status des Materials
      // Da wir 5 Boxen gleichzeitig verarbeiten, erhöhen wir boxesProcessed um 5
      const updatedMaterials = [...materials];
      updatedMaterials[currentMaterialIndex] = {
        ...updatedMaterials[currentMaterialIndex],
        boxesProcessed: updatedMaterials[currentMaterialIndex].boxesProcessed + scales.length,
        weight: totalNettoWeight // Speichere das Netto-Gewicht bei Linienwaage
      };
      // Aktualisiere den Materials-State, damit die UI sofort aktualisiert wird
      setMaterials(updatedMaterials);
      // Wenn alle Boxen des aktuellen Materials gefüllt wurden, markiere es als abgeschlossen
      if (updatedMaterials[currentMaterialIndex].boxesProcessed >= updatedMaterials[currentMaterialIndex].lotSize) {
        setFilledMaterials(prev => [...prev, currentMaterial.id]);
      }
      // Wait a moment to show the unload message before showing success feedback
      setTimeout(() => {
        setShowUnloadScaleMessage(false);
        // Zeige kurzes Erfolgsfeedback
        setShowSuccessFeedback(true);
        // Nach kurzer Zeit Feedback ausblenden und zum nächsten Material oder zur nächsten Box wechseln
        setTimeout(() => {
          setShowSuccessFeedback(false);
          // Wenn alle Boxen des aktuellen Materials gefüllt wurden, zum nächsten Material wechseln
          if (updatedMaterials[currentMaterialIndex].boxesProcessed >= updatedMaterials[currentMaterialIndex].lotSize) {
            if (currentMaterialIndex < materials.length - 1) {
              setCurrentMaterialIndex(prev => prev + 1);
            }
          }
          // Reset der Scan-Daten für die nächste Box nur wenn wir zu einem neuen Material wechseln
          // und dieses noch nicht validiert wurde
          if (updatedMaterials[currentMaterialIndex].boxesProcessed >= updatedMaterials[currentMaterialIndex].lotSize) {
            setScannedCode('');
            setScanSuccess(null);
            setErrorMessage('');
          }
          // Zurücksetzen der Wiegedaten
          resetAllScales();
          setWeighingStep('tara');
        }, 1500);
      }, 2000);
    } else {
      // Bei Ausgangswaage: Einzelne Kiste bestätigen
      // Erhöhe die Anzahl der bestätigten Kisten
      const newConfirmedBoxes = confirmedBoxes + 1;
      setConfirmedBoxes(newConfirmedBoxes);
      // Wenn alle Kisten bestätigt wurden, markiere das Material als abgeschlossen
      if (newConfirmedBoxes >= currentMaterial.lotSize) {
        // Aktualisiere den Status des Materials
        const updatedMaterials = [...materials];
        updatedMaterials[currentMaterialIndex] = {
          ...updatedMaterials[currentMaterialIndex],
          boxesProcessed: currentMaterial.lotSize
        };
        setMaterials(updatedMaterials);
        setFilledMaterials(prev => [...prev, currentMaterial.id]);
        // Verwiegungsauftrag erstellen
        onWeighingRequired(currentMaterial.id, currentMaterial.orderId);
        // Zeige kurzes Erfolgsfeedback
        setShowSuccessFeedback(true);
        // Nach kurzer Zeit Feedback ausblenden und zum nächsten Material wechseln
        setTimeout(() => {
          setShowSuccessFeedback(false);
          // Wenn es noch weitere Materialien gibt, wechsle zum nächsten
          if (currentMaterialIndex < materials.length - 1) {
            setCurrentMaterialIndex(prev => prev + 1);
          }
          // Reset der Scan-Daten für das nächste Material
          setScannedCode('');
          setScanSuccess(null);
          setErrorMessage('');
          setConfirmedBoxes(0); // Zurücksetzen der bestätigten Kisten
        }, 1500);
      }
    }
  };
  const getLineName = (id: string) => {
    const lineNames: {
      [key: string]: string;
    } = {
      tk1: 'TK Linie 1',
      tk2: 'TK Linie 2',
      tk3: 'TK Linie 3',
      tk4: 'TK Linie 4'
    };
    return lineNames[id] || id;
  };
  // Funktion zur Generierung einer Chargenummer
  const generateBatchNumber = (materialId: string) => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${materialId.slice(1, 4)}-${year}${month}${day}-${random}`;
  };
  const handleScan = () => {
    if (!scannedCode) {
      setErrorMessage('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simuliere den Scanvorgang mit einer kurzen Verzögerung
    setTimeout(() => {
      // Validiere, ob der gescannte Code mit der Material-ID übereinstimmt oder mit dem Material zusammenhängt
      const isValid = scannedCode.includes(currentMaterial.id) || scannedCode.startsWith('P' + currentMaterial.id.substring(1));
      setScanSuccess(isValid);
      setIsValidated(isValid);
      if (isValid) {
        setErrorMessage('');
        // Generiere eine Chargenummer für das gescannte Material
        const newBatchNumber = generateBatchNumber(currentMaterial.id);
        setBatchNumber(newBatchNumber);
        // Markiere das gesamte Lot als validiert
        const updatedMaterials = [...materials];
        updatedMaterials[currentMaterialIndex] = {
          ...updatedMaterials[currentMaterialIndex],
          lotValidated: true
        };
        setMaterials(updatedMaterials);
      } else {
        setErrorMessage('Der gescannte Code gehört nicht zu diesem Rohstoff. Bitte überprüfen Sie den Rohstoff.');
        setBatchNumber('');
      }
      setIsScanning(false);
    }, 1000);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };
  // Berechne den Fortschritt für das aktuelle Lot
  const currentLotProgress = currentMaterial.boxesProcessed / currentMaterial.lotSize * 100;
  // Berechne die Lot-Gesamtmasse und Toleranz
  const lotTotalTargetWeight = currentMaterial.quantity * currentMaterial.lotSize;
  const lotTolerance = lotTotalTargetWeight * 0.05; // 5% Toleranz
  return <div className="container mx-auto" data-id="element-2108">
      {showSuccessFeedback && <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50" data-id="element-2109">
          <div className="bg-white rounded-xl p-6 max-w-md text-center" data-id="element-2110">
            <div className="bg-green-100 p-3 rounded-full inline-flex mb-3" data-id="element-2111">
              <CheckCircleIcon size={40} className="text-green-600" data-id="element-2112" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-1" data-id="element-2113">
              {weighingMode === 'line' ? 'Material erfolgreich verwogen!' : confirmedBoxes >= currentMaterial.lotSize ? 'Alle Kisten befüllt!' : 'Kiste erfolgreich befüllt!'}
            </h3>
            <p className="text-gray-600 mb-2" data-id="element-2114">
              {weighingMode === 'line' ? `${scales.length} Boxen wurden erfasst.` : confirmedBoxes >= currentMaterial.lotSize ? `${currentMaterial.lotSize} Kisten zur Verwiegung weitergeleitet.` : `Kiste ${confirmedBoxes} von ${currentMaterial.lotSize}`}
            </p>
            <div className="text-sm text-gray-500 mt-2" data-id="element-2115">
              {weighingMode === 'line' || confirmedBoxes >= currentMaterial.lotSize ? 'Automatische Weiterleitung...' : 'Fahren Sie mit der nächsten Kiste fort.'}
            </div>
          </div>
        </div>}

      <div className="mb-4" data-id="element-2116">
        <div className="flex items-center justify-between" data-id="element-2117">
          <div data-id="element-2118">
            <h2 className="text-xl font-bold text-gray-800 mb-1" data-id="element-2119">
              {getLineName(lineId)}
            </h2>
            <p className="text-sm text-gray-600" data-id="element-2120">
              Auftrag #{currentMaterial.orderId}: {currentMaterial.orderName}
              {weighingMode === 'line' ? ' (Linienwaage)' : ' (Ausgangswaage)'}
            </p>
          </div>
          <button onClick={onBack} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-gray-700 hover:bg-gray-100" data-id="element-2121">
            <ArrowLeftIcon size={16} data-id="element-2122" />
            <span data-id="element-2123">Zurück</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2124">
        <div className="lg:col-span-2" data-id="element-2125">
          <div className="bg-white rounded-xl shadow-md p-6" data-id="element-2126">
            {/* Enhanced progress display */}
            <div className="mb-6" data-id="element-2127">
              <div className="flex justify-between items-center mb-2" data-id="element-2128">
                <span className="font-medium text-gray-700 text-base" data-id="element-2129">
                  Gesamtfortschritt
                </span>
                <span className="font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full text-sm" data-id="element-2130">
                  {filledMaterials.length} von {materials.length} Materialien
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3" data-id="element-2131">
                <div className="bg-[#8DC63F] h-3 rounded-full transition-all duration-500" style={{
                width: `${progress}%`
              }} data-id="element-2132"></div>
              </div>
            </div>
            {/* Enhanced material display */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6" data-id="element-2133">
              <div className="flex flex-col sm:flex-row items-center gap-6 mb-4" data-id="element-2134">
                {/* Material info card */}
                <div className="bg-white rounded-xl p-5 shadow-sm text-center w-full sm:w-auto sm:min-w-[220px]" data-id="element-2135">
                  <h3 className="text-xl font-bold text-blue-800 mb-2" data-id="element-2136">
                    {currentMaterial.name}
                  </h3>
                  <div className="text-sm text-gray-600 mb-2" data-id="element-2137">
                    <div data-id="element-2138">
                      Material-ID:{' '}
                      <span className="font-medium" data-id="element-2139">{currentMaterial.id}</span>
                    </div>
                    <div data-id="element-2140">
                      Lot:{' '}
                      <span className="font-medium" data-id="element-2141">
                        {currentMaterial.lotId}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm font-medium bg-blue-100 text-blue-800 px-3 py-1 rounded-full mb-3 inline-block" data-id="element-2142">
                    Box {currentMaterial.boxesProcessed + 1}/
                    {currentMaterial.lotSize}
                  </div>
                  <div className="text-2xl font-bold text-gray-800 p-2 border-t border-gray-100" data-id="element-2143">
                    {currentMaterial.quantity} {currentMaterial.unit}
                  </div>
                </div>
                {/* Scan & instructions area */}
                <div className="flex-1 w-full" data-id="element-2144">
                  {/* Barcode scan area */}
                  {!currentMaterial.lotValidated && currentMaterial.boxesProcessed === 0 ? <div className="mb-4 bg-white p-4 rounded-lg border border-gray-200" data-id="element-2145">
                      <label className="block font-medium text-gray-700 mb-2" data-id="element-2146">
                        Barcode scannen zur Lot-Validierung:
                      </label>
                      <div className="flex gap-3" data-id="element-2147">
                        <div className="flex-1 relative" data-id="element-2148">
                          <input ref={inputRef} type="text" className={`block w-full pl-10 pr-3 py-3 text-base border-2 ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`} placeholder="Barcode scannen..." value={scannedCode} onChange={e => setScannedCode(e.target.value)} onKeyDown={handleKeyDown} disabled={isValidated} data-id="element-2149" />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2150">
                            <ScanLineIcon size={20} className="text-gray-400" data-id="element-2151" />
                          </div>
                        </div>
                        <button onClick={handleScan} className={`py-3 px-5 rounded-lg ${isValidated ? 'bg-green-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white font-medium flex items-center gap-2 min-w-[120px] justify-center`} disabled={isValidated || isScanning} data-id="element-2152">
                          {isScanning ? 'Scanne...' : 'Scannen'}
                        </button>
                      </div>
                      {scanSuccess === true && <div className="mt-3 flex items-center text-green-600 bg-green-50 p-2 rounded-lg border border-green-200" data-id="element-2153">
                          <CheckCircleIcon size={20} className="mr-2" data-id="element-2154" />
                          <span data-id="element-2155">
                            Lot validiert! Chargen-Nr:{' '}
                            <span className="font-bold" data-id="element-2156">{batchNumber}</span>
                          </span>
                        </div>}
                      {scanSuccess === false && <div className="mt-3 flex items-center text-red-600 bg-red-50 p-2 rounded-lg border border-red-200" data-id="element-2157">
                          <XCircleIcon size={20} className="mr-2" data-id="element-2158" />
                          <span data-id="element-2159">{errorMessage}</span>
                        </div>}
                    </div> : currentMaterial.lotValidated && <div className="mb-4 flex items-center text-green-600 bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-2160">
                        <CheckCircleIcon size={20} className="mr-2" data-id="element-2161" />
                        <span data-id="element-2162">
                          Lot validiert! Chargen-Nr:{' '}
                          <span className="font-bold" data-id="element-2163">{batchNumber}</span>
                        </span>
                      </div>}
                  {/* Instructions */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200 mb-4" data-id="element-2164">
                    <h4 className="font-medium text-gray-800 mb-2" data-id="element-2165">
                      Anweisungen:
                    </h4>
                    <p className="text-gray-600" data-id="element-2166">
                      {weighingMode === 'line' ? 'Füllen Sie die Kisten und wiegen Sie sie direkt an der Linie. Sie können beliebig viele Waagen gleichzeitig oder einzeln verwenden.' : 'Füllen Sie die Kisten vollständig mit dem Material. Die Kisten werden später im Verwiegungsmodul verwogen.'}
                    </p>
                  </div>
                  {/* Progress bar for output weighing mode */}
                  {weighingMode === 'output' && <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4" data-id="element-2167">
                      <div className="flex justify-between items-center mb-2" data-id="element-2168">
                        <span className="font-medium text-gray-700" data-id="element-2169">
                          Befüllte Kisten
                        </span>
                        <span className="font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full" data-id="element-2170">
                          {confirmedBoxes}/{currentMaterial.lotSize}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 mb-1" data-id="element-2171">
                        <div className="bg-green-500 h-3 rounded-full transition-all duration-500" style={{
                      width: `${confirmedBoxes / currentMaterial.lotSize * 100}%`
                    }} data-id="element-2172"></div>
                      </div>
                    </div>}
                </div>
              </div>
              {/* Enhanced weighing scale control section */}
              {weighingMode === 'line' && isValidated && <div className="mt-4 pt-5 border-t border-blue-200" data-id="element-2173">
                  {/* Step indicator */}
                  <div className="mb-5 flex justify-center" data-id="element-2174">
                    <div className="flex items-center bg-white rounded-full px-2 py-1 border border-gray-200 shadow-sm" data-id="element-2175">
                      <div className={`px-4 py-2 rounded-full flex items-center gap-2 ${weighingStep === 'tara' ? 'bg-blue-100 text-blue-800 font-medium' : 'text-gray-500'}`} data-id="element-2176">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${weighingStep === 'tara' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-2177">
                          1
                        </div>
                        <span data-id="element-2178">Tara-Gewicht</span>
                      </div>
                      <div className="mx-2 text-gray-300" data-id="element-2179">→</div>
                      <div className={`px-4 py-2 rounded-full flex items-center gap-2 ${weighingStep === 'brutto' ? 'bg-blue-100 text-blue-800 font-medium' : 'text-gray-500'}`} data-id="element-2180">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${weighingStep === 'brutto' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-2181">
                          2
                        </div>
                        <span data-id="element-2182">Material wiegen</span>
                      </div>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex justify-center gap-4 mb-6" data-id="element-2183">
                    {weighingStep === 'tara' ? <button onClick={startAllTaraWeighing} disabled={isAllScalesTaring} className={`
                          bg-blue-500 hover:bg-blue-600 text-white px-5 py-3 rounded-lg 
                          font-medium transition-colors flex items-center gap-2 min-w-[180px] justify-center
                          ${isAllScalesTaring ? 'opacity-70 cursor-not-allowed' : ''}
                        `} data-id="element-2184">
                        <RefreshCwIcon size={18} data-id="element-2185" />
                        <span data-id="element-2186">
                          {isAllScalesTaring ? 'Tariere...' : 'Alle Waagen tarieren'}
                        </span>
                      </button> : <button onClick={startAllBruttoWeighing} disabled={isAllScalesWeighing || !scales.some(s => s.taraWeight !== null)} className={`
                          bg-blue-500 hover:bg-blue-600 text-white px-5 py-3 rounded-lg 
                          font-medium transition-colors flex items-center gap-2 min-w-[180px] justify-center
                          ${isAllScalesWeighing || !scales.some(s => s.taraWeight !== null) ? 'opacity-70 cursor-not-allowed' : ''}
                        `} data-id="element-2187">
                        <ScaleIcon size={18} data-id="element-2188" />
                        <span data-id="element-2189">
                          {isAllScalesWeighing ? 'Wiege...' : 'Alle Waagen wiegen'}
                        </span>
                      </button>}
                    {weighingStep === 'tara' && scales.some(s => s.taraWeight !== null) && <button onClick={() => setWeighingStep('brutto')} className="bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-lg font-medium min-w-[180px]" data-id="element-2190">
                          Weiter zu Schritt 2
                        </button>}
                    {weighingStep === 'brutto' && <button onClick={() => {
                  resetAllScales();
                  setWeighingStep('tara');
                }} className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-3 rounded-lg font-medium min-w-[180px]" data-id="element-2191">
                        Zurück zu Schritt 1
                      </button>}
                  </div>
                  {/* Enhanced scale display */}
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-5" data-id="element-2192">
                    {scales.map(scale => <div key={scale.id} className={`
                          rounded-xl p-4 border-2 shadow-sm
                          ${scale.nettoWeight !== null ? scale.isWithinTolerance ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50' : scale.taraWeight !== null ? 'border-blue-300 bg-blue-50' : 'border-gray-300 bg-white'}
                        `} data-id="element-2193">
                        <div className="flex justify-between items-center mb-3" data-id="element-2194">
                          <h6 className="font-medium text-gray-800" data-id="element-2195">
                            {scale.name}
                          </h6>
                          {scale.nettoWeight !== null && (scale.isWithinTolerance ? <CheckCircleIcon size={20} className="text-green-600" data-id="element-2196" /> : <XCircleIcon size={20} className="text-red-600" data-id="element-2197" />)}
                        </div>
                        {/* Tara weight section */}
                        <div className="mb-3 bg-white bg-opacity-70 p-3 rounded-lg border border-gray-200" data-id="element-2198">
                          <div className="flex justify-between items-center mb-1" data-id="element-2199">
                            <span className="text-sm font-medium text-gray-700" data-id="element-2200">
                              Tara:
                            </span>
                            <span className={`font-medium ${scale.taraWeight !== null ? 'text-blue-700' : 'text-gray-400'}`} data-id="element-2201">
                              {scale.taraWeight !== null ? `${scale.taraWeight} ${currentMaterial.unit}` : '--'}
                            </span>
                          </div>
                          {weighingStep === 'tara' && <>
                              {scale.taraWeight === null ? <button onClick={() => startTaraWeighing(scale.id)} disabled={scale.isWeighingTara} className={`
                                    mt-2 w-full bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg
                                    font-medium transition-colors flex items-center justify-center gap-2
                                    ${scale.isWeighingTara ? 'opacity-70 cursor-not-allowed' : ''}
                                  `} data-id="element-2202">
                                  <RefreshCwIcon size={16} data-id="element-2203" />
                                  {scale.isWeighingTara ? 'Tariere...' : 'Tarieren'}
                                </button> : <button onClick={() => startBruttoWeighing(scale.id)} disabled={scale.isWeighingBrutto} className={`
                                    mt-2 w-full bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg
                                    font-medium transition-colors flex items-center justify-center gap-2
                                    ${scale.isWeighingBrutto ? 'opacity-70 cursor-not-allowed' : ''}
                                  `} data-id="element-2204">
                                  <ScaleIcon size={16} data-id="element-2205" />
                                  {scale.isWeighingBrutto ? 'Wiege...' : 'Wiegen'}
                                </button>}
                            </>}
                        </div>
                        {/* Netto weight section */}
                        {weighingStep === 'brutto' && <div className="bg-white bg-opacity-70 p-3 rounded-lg border border-gray-200" data-id="element-2206">
                            {scale.taraWeight !== null ? <>
                                <div className="flex justify-between items-center mb-1" data-id="element-2207">
                                  <span className="text-sm font-medium text-gray-700" data-id="element-2208">
                                    Netto:
                                  </span>
                                  <span className={`font-medium ${scale.nettoWeight !== null ? scale.isWithinTolerance ? 'text-green-700' : 'text-red-700' : 'text-gray-400'}`} data-id="element-2209">
                                    {scale.nettoWeight !== null ? `${scale.nettoWeight} ${currentMaterial.unit}` : '--'}
                                  </span>
                                </div>
                                {scale.nettoWeight === null ? <button onClick={() => startBruttoWeighing(scale.id)} disabled={scale.isWeighingBrutto} className={`
                                      mt-2 w-full bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg
                                      font-medium transition-colors flex items-center justify-center gap-2
                                      ${scale.isWeighingBrutto ? 'opacity-70 cursor-not-allowed' : ''}
                                    `} data-id="element-2210">
                                    <ScaleIcon size={16} data-id="element-2211" />
                                    {scale.isWeighingBrutto ? 'Wiege...' : 'Wiegen'}
                                  </button> : !scale.isWithinTolerance && <button onClick={() => startBruttoWeighing(scale.id)} className="mt-2 w-full bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg font-medium flex items-center justify-center gap-2" data-id="element-2212">
                                      <RefreshCwIcon size={16} data-id="element-2213" />
                                      Neu wiegen
                                    </button>}
                              </> : <div className="flex items-center justify-center text-amber-600 py-2" data-id="element-2214">
                                <AlertTriangleIcon size={16} className="mr-2" data-id="element-2215" />
                                Bitte zuerst tarieren
                              </div>}
                          </div>}
                      </div>)}
                  </div>
                  {/* Active scales summary */}
                  {weighingStep === 'brutto' && scales.some(s => s.nettoWeight !== null) && <div className={`
                      p-5 rounded-xl mb-4 text-center border-2 shadow-sm
                      ${scales.filter(s => s.nettoWeight !== null).every(s => s.isWithinTolerance) ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'}
                    `} data-id="element-2216">
                        <div className="text-xl font-bold mb-1" data-id="element-2217">
                          Gesamtgewicht der aktiven Waagen:{' '}
                          {calculateTotalNettoWeight().toFixed(2)}{' '}
                          {currentMaterial.unit}
                        </div>
                        <div className="text-sm mb-2" data-id="element-2218">
                          <span className="font-medium" data-id="element-2219">Aktive Waagen:</span>{' '}
                          {scales.filter(s => s.nettoWeight !== null).length}{' '}
                          von {scales.length}
                        </div>
                        <div className={`font-medium ${scales.filter(s => s.nettoWeight !== null).every(s => s.isWithinTolerance) ? 'text-green-700' : 'text-red-700'}`} data-id="element-2220">
                          {scales.filter(s => s.nettoWeight !== null).every(s => s.isWithinTolerance) ? 'Alle aktiven Waagen in Toleranz ✓' : 'Achtung: Waagen außerhalb Toleranz! Bitte korrigieren Sie die Werte.'}
                        </div>
                      </div>}
                </div>}
              {/* Confirmation button */}
              <div className="mt-5 text-center" data-id="element-2221">
                {showUnloadScaleMessage ? <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4" data-id="element-2222">
                    <p className="text-amber-700 font-medium flex items-center justify-center" data-id="element-2223">
                      <AlertTriangleIcon size={20} className="mr-2" data-id="element-2224" />
                      Bitte entlasten Sie die Waage(n)
                    </p>
                  </div> : <button onClick={handleMaterialFilled} disabled={!isValidated || weighingMode === 'line' && (scales.filter(s => s.nettoWeight !== null).length === 0 || !scales.filter(s => s.nettoWeight !== null).every(s => s.isWithinTolerance))} className={`
                      bg-[#8DC63F] hover:bg-[#7AB929] text-white px-6 py-3 rounded-xl
                      text-lg font-medium transition-colors flex items-center gap-2 mx-auto
                      ${!isValidated || weighingMode === 'line' && (scales.filter(s => s.nettoWeight !== null).length === 0 || !scales.filter(s => s.nettoWeight !== null).every(s => s.isWithinTolerance)) ? 'opacity-50 cursor-not-allowed' : 'shadow-md hover:shadow-lg'}
                    `} data-id="element-2225">
                    <CheckIcon size={22} data-id="element-2226" />
                    <span data-id="element-2227">
                      {weighingMode === 'line' ? `${scales.filter(s => s.nettoWeight !== null).length} Kiste${scales.filter(s => s.nettoWeight !== null).length !== 1 ? 'n' : ''} bestätigen` : confirmedBoxes >= currentMaterial.lotSize - 1 ? 'Letzte Kiste bestätigen' : `Kiste ${confirmedBoxes + 1} bestätigen`}
                    </span>
                  </button>}
              </div>
            </div>
            {/* Enhanced instructions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-id="element-2228">
              <h5 className="font-medium text-yellow-700 mb-1 flex items-center" data-id="element-2229">
                <AlertCircleIcon size={16} className="mr-1" data-id="element-2230" />
                <span data-id="element-2231">Kurzanleitung</span>
              </h5>
              <ul className="text-sm text-yellow-600 list-disc pl-5 space-y-0.5" data-id="element-2232">
                {weighingMode === 'line' ? <>
                    <li data-id="element-2233">Scannen Sie den Barcode zur Lot-Validierung</li>
                    <li data-id="element-2234">Tarieren Sie alle Waagen (Schritt 1)</li>
                    <li data-id="element-2235">Wiegen Sie das Material (Schritt 2)</li>
                    <li data-id="element-2236">Bestätigen Sie, wenn alle Waagen in Toleranz sind</li>
                  </> : <>
                    <li data-id="element-2237">Scannen Sie den Barcode zur Lot-Validierung</li>
                    <li data-id="element-2238">Füllen Sie jede Kiste mit dem Rohstoff</li>
                    <li data-id="element-2239">Bestätigen Sie jede Kiste einzeln</li>
                    <li data-id="element-2240">Kisten werden später im Verwiegungsmodul verwogen</li>
                  </>}
              </ul>
            </div>
          </div>
        </div>
        {/* Enhanced sidebar */}
        <div className="lg:col-span-1" data-id="element-2241">
          <div className="bg-white rounded-xl shadow-md p-4" data-id="element-2242">
            {/* Lot information */}
            <div className="mb-4" data-id="element-2243">
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-2244">
                <PackageIcon size={16} className="mr-1" data-id="element-2245" />
                <span data-id="element-2246">Lot: {currentMaterial.lotId}</span>
              </h4>
              <div className="bg-blue-50 p-3 rounded-lg" data-id="element-2247">
                <div className="grid grid-cols-2 gap-1 text-sm mb-2" data-id="element-2248">
                  <div className="text-gray-600" data-id="element-2249">Material:</div>
                  <div className="font-medium text-right" data-id="element-2250">
                    {currentMaterial.name}
                  </div>
                  <div className="text-gray-600" data-id="element-2251">Box-Gewicht:</div>
                  <div className="font-medium text-right" data-id="element-2252">
                    {currentMaterial.quantity} {currentMaterial.unit}
                  </div>
                  <div className="text-gray-600" data-id="element-2253">Boxen:</div>
                  <div className="font-medium text-right" data-id="element-2254">
                    {currentMaterial.boxesProcessed}/{currentMaterial.lotSize}
                  </div>
                  <div className="text-gray-600" data-id="element-2255">Status:</div>
                  <div className={`font-medium text-right ${currentMaterial.lotValidated ? 'text-green-600' : 'text-yellow-600'}`} data-id="element-2256">
                    {currentMaterial.lotValidated ? 'Validiert' : 'Nicht validiert'}
                  </div>
                  {weighingMode === 'line' && allScalesHaveNettoWeight() && <>
                      <div className="text-gray-600" data-id="element-2257">Aktuelles Gewicht:</div>
                      <div className={`font-medium text-right ${areAllScalesWithinTolerance() ? 'text-green-600' : 'text-red-600'}`} data-id="element-2258">
                        {calculateTotalNettoWeight().toFixed(2)}{' '}
                        {currentMaterial.unit}
                      </div>
                    </>}
                </div>
                <div className="mt-2" data-id="element-2259">
                  <div className="flex justify-between text-xs mb-1" data-id="element-2260">
                    <span data-id="element-2261">
                      Box {currentMaterial.boxesProcessed + 1}/
                      {currentMaterial.lotSize}
                    </span>
                    <span data-id="element-2262">{Math.round(currentLotProgress)}% abgeschlossen</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-1.5" data-id="element-2263">
                    <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" style={{
                    width: `${currentLotProgress}%`
                  }} data-id="element-2264"></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Material overview */}
            <div data-id="element-2265">
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-2266">
                <ClipboardListIcon size={16} className="mr-1" data-id="element-2267" />
                <span data-id="element-2268">Materialübersicht</span>
              </h4>
              <div className="space-y-2" data-id="element-2269">
                {materials.map((material, index) => <div key={material.id} className={`border rounded-lg p-2 ${index === currentMaterialIndex ? 'bg-blue-50 border-blue-300' : filledMaterials.includes(material.id) ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`} data-id="element-2270">
                    <div className="flex justify-between items-start" data-id="element-2271">
                      <div data-id="element-2272">
                        <div className="font-medium text-sm" data-id="element-2273">
                          {material.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-2274">
                          Lot: {material.lotId}
                        </div>
                      </div>
                      {filledMaterials.includes(material.id) ? <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full" data-id="element-2275">
                          Fertig
                        </span> : index === currentMaterialIndex ? <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full" data-id="element-2276">
                          Aktiv
                        </span> : <span className="text-xs bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded-full" data-id="element-2277">
                          Warten
                        </span>}
                    </div>
                    {(index === currentMaterialIndex || material.boxesProcessed > 0) && <div className="mt-1 w-full bg-gray-200 rounded-full h-1" data-id="element-2278">
                        <div className={`h-1 rounded-full transition-all duration-300 ${filledMaterials.includes(material.id) ? 'bg-green-500' : 'bg-blue-500'}`} style={{
                    width: `${material.boxesProcessed / material.lotSize * 100}%`
                  }} data-id="element-2279"></div>
                      </div>}
                    <div className="flex justify-between text-xs mt-1" data-id="element-2280">
                      <span data-id="element-2281">
                        {material.boxesProcessed}/{material.lotSize} Boxen
                      </span>
                      {weighingMode === 'line' ? <span data-id="element-2282">
                          {material === currentMaterial ? `${confirmedBoxes}/${material.lotSize} befüllt` : ''}
                        </span> : <span data-id="element-2283">
                          {material === currentMaterial ? `${confirmedBoxes}/${material.lotSize} befüllt` : ''}
                        </span>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}